/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import React, { FunctionComponent } from 'react';
import PropTypes from 'prop-types';
import { DeviceFeatures } from '../../../core/device';
import { ServicesClientImpl } from '../../../core/impls';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { START_RATING_MAX_STARS } from '../../../config';
import { OfferPreviewFormatter } from './OfferPreviewFormatter';
import { PreviewPaneProps } from './PreviewPaneProps';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';

import './PreviewPane.scss';

const PreviewPane: FunctionComponent<PreviewPaneProps> = ({ offerPreview }) => {
    const oOfferPreviewFormatter: OfferPreviewFormatter = new OfferPreviewFormatter(offerPreview);

    const movieYearDiv = oOfferPreviewFormatter.hasMoviePreview() ? (
        <div className="movie-year">{oOfferPreviewFormatter.getMovieYear()}</div>
    ) : null;
    const services = new ServicesClientImpl();
    const newIcon = services.getAppUiImageByKey(BrandingUiInfoKeys.APP_ICONS.NEW_ICON);
    const isNewDiv = oOfferPreviewFormatter.hasNewIcon() ? (
        <img src={newIcon?.imageUrl} className={`is-new ${newIcon?.imageType}`} />
    ) : null;

    const ccVideoResolutionText = oOfferPreviewFormatter.getCcVideoResolution();

    const contentInfoDiv = (
        <div className="content-info">
            {ccVideoResolutionText.length > 0 ? (
                <div className="cc_stream">{ccVideoResolutionText}</div>
            ) : null}
            <div className="category-first-aired">
                {oOfferPreviewFormatter.getCategoryAndFirstAired()}
            </div>
        </div>
    );

    const internalRatingsDiv = oOfferPreviewFormatter
        .getInternalRatings()
        .map((ratingName, index) => {
            return (
                <span className="internal-ratings" key={index}>
                    <span className="rating-text">{ratingName}</span>
                </span>
            );
        });

    const starRating = oOfferPreviewFormatter.getStarRating();
    const starRatingDiv = oOfferPreviewFormatter.hasStarRating() ? (
        <span className="star-rating">
            {new Array(Math.floor(starRating)).fill(0).map((_, index) => {
                return <span className="star-icon full" key={index}></span>;
            })}
            {starRating > Math.floor(starRating) ? <span className="star-icon half"></span> : null}
            {new Array(START_RATING_MAX_STARS - Math.floor(starRating)).fill(0).map((_, index) => {
                return <span className="star-icon empty" key={index}></span>;
            })}
        </span>
    ) : null;

    const contentAiringInfo = (
        <div className="content-airing-info">
            {oOfferPreviewFormatter.getContentAiringDateTime()}
            {internalRatingsDiv}
            {starRatingDiv}
        </div>
    );

    if (oOfferPreviewFormatter.isAvailableInSocu()) {
        // TODO: add logging
    }

    const providerImagesDiv = DeviceFeatures.isSocuEnabled() ? (
        <div className="provider-images">
            {oOfferPreviewFormatter.getProvidersImageUrls()?.map((url: string, index: number) => (
                <ImageLoader
                    key={index}
                    src={url}
                    imageType={ImageLoaderTypes.PROVIDER_IMAGE_PREVIEW_ICON}
                />
            ))}
        </div>
    ) : null;

    return (
        <React.Fragment>
            <div className={oOfferPreviewFormatter.getPreviewTitleClasses()}>
                {oOfferPreviewFormatter.getPreviewTitle()}
                {oOfferPreviewFormatter.getStreamType() ? (
                    <div
                        className={`preview-stream-type-icon ${oOfferPreviewFormatter.getStreamType()}`}
                    ></div>
                ) : null}
                <div
                    className={`title-attributes ${
                        oOfferPreviewFormatter.hasPreviewTitleTruncation() ? 'truncate' : ''
                    }`}
                >
                    {oOfferPreviewFormatter.hasPreviewTitleTruncation() ? (
                        <div className="transparent-effect">&nbsp;</div>
                    ) : null}
                    <div className="other-details">
                        {movieYearDiv}
                        {isNewDiv}
                    </div>
                </div>
            </div>
            <div className={oOfferPreviewFormatter.getPreviewDescriptionClasses()}>
                {oOfferPreviewFormatter.getPreviewDescription()}
                <div
                    className={`description-attributes ${
                        oOfferPreviewFormatter.hasPreviewDescriptionTruncation() ? 'truncate' : ''
                    }`}
                >
                    {oOfferPreviewFormatter.hasPreviewDescriptionTruncation() ? (
                        <div className="transparent-effect"></div>
                    ) : null}
                    {contentInfoDiv}
                </div>
            </div>
            {contentAiringInfo}
            {providerImagesDiv}
        </React.Fragment>
    );
};

PreviewPane.propTypes = {
    offerPreview: PropTypes.any.isRequired,
};
export default PreviewPane;
